<template>
  <div>
    <footer class="sct2-footer">
      <div v-if="getBatteryLife" class="sct2-footer-information">
        <div class="sct2-footer-text">
          {{ $filters.capitalize($t('estimated_batteryLife')) }}:
          {{ $filters.capitalize(inputBatteryLifeText('betweenMonthsRange', getBatteryLife.min, getBatteryLife.max)) }}
        </div>
        <information class="sct2-footer-icon cursor-pointer" @click="showBatteryInfo = true" />
      </div>
      <div class="sct2-footer-buttons">
        <sdc-button :disabled="!getIsParamsValid || !hasParamsChanged" @click="sct2ParametersValidation">
          <span v-if="!isValidated">{{ $t('Save') }}</span>
          <span v-else class="sct2-footer-saved">
            {{ $t('Saved') }}
            <check :icon-color="'white'" />
          </span>
        </sdc-button>
      </div>
    </footer>

    <!-- Battery life info modal -->
    <Modal v-model="showBatteryInfo">
      <div class="sct2-modal-body">
        <img class="sct2-modal-img" src="../../assets/icons/battery.png" />
        <div class="sct2-modal-title">{{ $filters.capitalize($t('estimated_batteryLife')) }}:</div>
        <div class="sct2-modal-content">
          <div>
            {{ $t('sct2_modal_content') }}
          </div>
          <div class="sct2-modal-content-major">{{ $filters.capitalize($t('minBatteryLife')) }}:</div>
          <div>
            {{ $t('hoursPerDay', { n: 8 }) }}
          </div>
          <div class="sct2-modal-content-major">{{ $filters.capitalize($t('maxBatteryLife')) }}:</div>
          <div>
            {{ $t('hoursPerDay', { n: 8 }) }}
          </div>
        </div>
      </div>
    </Modal>

    <!-- Advanced modal -->
    <Modal v-model="showAdvanced">
      <div class="sct2-advanced-body">
        <div class="sct2-advanced-title">{{ $t('advanced') }}</div>
        <div class="sct2-advanced-actions">
          <button
            v-if="hasRequestedMode"
            class="sct2-advanced-action-btn inline-block cursor-pointer border border-[#ffc107] bg-transparent text-center text-[#ffc107] transition-colors select-none hover:bg-[#ffc107] hover:text-[#212529] focus:outline-none"
            @click="resetRequestedMode"
          >
            {{ $t('reset_pending_mode') }}
          </button>
          <button
            class="sct2-advanced-action-btn inline-block cursor-pointer border border-[#6c757d] bg-transparent text-center text-[#6c757d] transition-colors select-none hover:bg-[#6c757d] hover:text-white focus:outline-none"
            @click="showBatteryConfirmModal"
          >
            {{ $t('reset_battery_level') }}
          </button>
        </div>
        <div v-if="advancedResult" class="sct2-advanced-result" :class="{ 'text-[#dc3545]!': advancedResult === 'error' }">
          {{ advancedResult }}
        </div>
      </div>
    </Modal>

    <!-- Battery confirm modal -->
    <Modal v-model="showBatteryConfirm">
      <div class="sct2-advanced-body">
        <div class="sct2-advanced-title">{{ $t('reset_battery_confirm') }}</div>
        <div class="sct2-advanced-actions">
          <button
            class="sct2-advanced-action-btn inline-block cursor-pointer border border-[#dc3545] bg-[#dc3545] text-center text-white transition-colors select-none hover:border-[#bd2130] hover:bg-[#c82333] focus:outline-none"
            @click="confirmBatteryReset"
          >
            {{ $t('yes') }}
          </button>
          <button
            class="sct2-advanced-action-btn inline-block cursor-pointer border border-[#6c757d] bg-transparent text-center text-[#6c757d] transition-colors select-none hover:bg-[#6c757d] hover:text-white focus:outline-none"
            @click="showBatteryConfirm = false"
          >
            {{ $t('cancel') }}
          </button>
        </div>
        <div v-if="batteryResult" class="sct2-advanced-result" :class="{ 'text-[#dc3545]!': batteryResult === 'error' }">
          {{ batteryResult }}
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import Check from '../../svg/check.vue'
import information from '../../svg/information.vue'
import Modal from '@/components/ui/Modal.vue'
import SdcButton from '@/components/ui/SDCButton.vue'
import httpClient from '../../api/httpClient'

export default {
  name: 'SCT2Footer',
  components: { information, Check, Modal, SdcButton },

  data() {
    return {
      isValidated: false,
      showBatteryInfo: false,
      showAdvanced: false,
      showBatteryConfirm: false,
      advancedResult: '',
      batteryResult: '',
    }
  },

  computed: {
    ...mapGetters('devices', ['getSelectedDevice']),
    ...mapGetters('parameters', ['getSct2Params', 'getBatteryLife', 'getIsParamsValid', 'hasParamsChanged', 'getRequestedMode']),

    hasRequestedMode() {
      return !!this.getRequestedMode
    },
  },

  mounted() {
    window.addEventListener('sct2:show-advanced', this.showAdvancedModal)
  },

  beforeUnmount() {
    window.removeEventListener('sct2:show-advanced', this.showAdvancedModal)
  },

  methods: {
    ...mapActions('parameters', ['patchSct2Params']),

    showAdvancedModal() {
      this.advancedResult = ''
      this.showAdvanced = true
    },

    showBatteryConfirmModal() {
      this.batteryResult = ''
      this.showAdvanced = false
      this.showBatteryConfirm = true
    },

    async sct2ParametersValidation() {
      if (this.getSelectedDevice) {
        await this.patchSct2Params({
          sct2_id: this.getSelectedDevice.id,
          sct2_mode_params: await this.getSct2Params,
        })

        const params = await this.getSct2Params
        if (params && params.requested_mode) {
          window.location.href = `/app/native/writemode?requested_mode=${params.requested_mode}`
          setTimeout(() => {
            window.location.href = '/exit'
          }, 200)
        } else {
          window.location.href = '/exit'
        }
      }
    },

    inputBatteryLifeText(i18nKey, min, max) {
      return this.$t(i18nKey, { n: min, m: max })
    },

    async resetRequestedMode() {
      if (!this.getSelectedDevice) return
      try {
        const params = await this.getSct2Params
        await this.patchSct2Params({
          sct2_id: this.getSelectedDevice.id,
          sct2_mode_params: { tlv_data: params.tlv_data },
        })
        this.advancedResult = this.$t('reset_pending_mode_success')
        setTimeout(() => {
          this.showAdvanced = false
        }, 1500)
        setTimeout(() => window.location.reload(), 1600)
      } catch (err) {
        console.log(err)
        this.advancedResult = 'error'
      }
    },

    async confirmBatteryReset() {
      if (!this.getSelectedDevice) return
      try {
        const res = await httpClient.patch('/devices/' + this.getSelectedDevice.id + '/battery_reset/', {})
        if (res.status === 200) {
          this.batteryResult = this.$t('request_reset_sct2_battery_success')
        } else if (res.status === 204) {
          this.batteryResult = this.$t('request_reset_sct2_battery_already_sent')
        }
        setTimeout(() => {
          this.showBatteryConfirm = false
        }, 2000)
      } catch (err) {
        console.log(err)
        this.batteryResult = 'error'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.sct2-modal {
  &-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;
    padding: 1em;
    padding-bottom: 2em;
  }

  &-img {
    width: 140px;
    height: 140px;
  }

  &-title {
    font-family: 'Montserrat';
    font-style: normal;
    font-weight: 800;
    font-size: 28px;
    color: #050328;
    text-align: center;
  }

  &-content {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    color: #050328;
    text-align: center;

    &-major {
      font-weight: 600 !important;
    }
  }
}

.sct2-advanced-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1em;
  padding-top: 2em;
}

.sct2-advanced-title {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #050328;
}

.sct2-advanced-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  width: 100%;
}

.sct2-advanced-action-btn {
  width: 100%;
  padding: 0.75em;
  border-radius: 8px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 14px;
}

.sct2-advanced-result {
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #28a745;
}

.sct2-footer {
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1em;

  &-information {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5em;
  }

  &-icon {
    width: 14px;
    height: 14px;
    opacity: 0.5;
  }

  &-text {
    font-family: 'Open Sans', sans-serif;
    font-weight: 400;
    font-size: 13px;
    color: #555b62;

    // Footer renders over the page canvas (dark in dark mode); modal text stays dark
    // because Modal.vue is always white.
    @media (prefers-color-scheme: dark) {
      color: var(--color-text-secondary-dark);
    }
  }

  &-buttons {
    display: flex;
    gap: 0.75em;
  }

  &-saved {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5em;
  }
}

// Dark mode — the Modal box follows the OS theme, so the content text, which is dark on the
// white light-mode box, needs light variants on the dark box.
@media (prefers-color-scheme: dark) {
  .sct2-modal-title,
  .sct2-advanced-title {
    color: var(--color-text-title-dark);
  }

  .sct2-modal-content {
    color: var(--color-text-dark);
  }
}
</style>
